import type { DataProvider } from "@refinedev/core";
import { readItems, readItem, createItem, updateItem, deleteItem, aggregate, readUsers } from "./api.ts";
import { directus, DIRECTUS_URL } from "./directus";
import { pushUndo, inverseOfCreate, inverseOfUpdate, inverseOfDelete, isUndoable, normalizeOld } from "@/lib/undo";
import { notifySaved } from "@/lib/notifySaved";

const opMap: Record<string, string> = {
  eq: "_eq", ne: "_neq", lt: "_lt", gt: "_gt", lte: "_lte", gte: "_gte",
  contains: "_contains", containss: "_contains", icontains: "_icontains", startswith: "_starts_with",
  endswith: "_ends_with", in: "_in", nin: "_nin", null: "_null", nnull: "_nnull",
};

function buildFilter(filters?: any[]) {
  if (!filters?.length) return undefined;
  const conds: any[] = [];
  for (const f of filters) {
    if (f && "field" in f && opMap[f.operator]) {
      conds.push({ [f.field]: { [opMap[f.operator]]: f.value } });
    }
  }
  return conds.length ? { _and: conds } : undefined;
}

function sanitizePayload(vars: any): any {
  if (!vars || typeof vars !== "object") return vars;
  if (Array.isArray(vars)) return vars.map(sanitizePayload);
  const clean: Record<string, any> = {};
  for (const [k, v] of Object.entries(vars)) {
    if (v && typeof v === "object") {
      if ("nativeEvent" in v || "target" in v || "nodeType" in v || typeof (v as any).preventDefault === "function") {
        continue;
      }
      clean[k] = sanitizePayload(v);
    } else if (typeof v !== "function") {
      clean[k] = v;
    }
  }
  return clean;
}

export const dataProvider: DataProvider = {
  getApiUrl: () => DIRECTUS_URL,

  getList: async ({ resource, pagination, sorters, filters, meta }) => {
    const page = (pagination as any)?.currentPage ?? (pagination as any)?.current ?? 1;
    const pageSize = pagination?.pageSize ?? 25;
    const filter: any = buildFilter(filters as any[]);
    const sort = (sorters || []).map((s) => (s.order === "desc" ? "-" : "") + s.field);
    const query: any = { limit: pageSize, page, fields: meta?.fields ?? ["*"] };
    if (filter) query.filter = filter;
    if (sort.length) query.sort = sort;

    // directus_users is a system collection served from /users, not /items.
    const raw: any = await directus.request(
      resource === "directus_users" ? readUsers(query) : readItems(resource as any, query)
    );
    // Never hand a non-array to Refine/components: a partial error (e.g. a queried
    // field missing on this environment) can resolve to a non-array payload, which
    // would crash every `.filter/.map` downstream. Fail soft to an empty list.
    const data: any[] = Array.isArray(raw) ? raw : [];
    if (!Array.isArray(raw)) console.warn(`[dataProvider] ${resource} getList returned non-array; coercing to []`, raw);
    let total = (page - 1) * pageSize + data.length;
    if (data.length === pageSize && resource !== "directus_users") {
      try {
        const agg: any = await directus.request(
          aggregate(resource as any, { aggregate: { countDistinct: "id" }, ...(filter ? { query: { filter } } : {}) } as any)
        );
        total = Number(agg?.[0]?.countDistinct?.id ?? total);
      } catch { /* keep fallback */ }
    }
    return { data, total };
  },

  getOne: async ({ resource, id, meta }) => {
    const data: any = await directus.request(readItem(resource as any, id as string, { fields: meta?.fields ?? ["*"] }));
    return { data };
  },

  getMany: async ({ resource, ids, meta }) => {
    const data: any = await directus.request(
      readItems(resource as any, { filter: { id: { _in: ids } }, fields: meta?.fields ?? ["*"], limit: -1 } as any)
    );
    return { data };
  },

  create: async ({ resource, variables }) => {
    const cleanVars = sanitizePayload(variables);
    const data: any = await directus.request(createItem(resource as any, cleanVars as any));
    if (data?.id != null && isUndoable(resource as string)) pushUndo(inverseOfCreate(resource as string, data));
    return { data };
  },

  update: async ({ resource, id, variables }) => {
    const cleanVars = sanitizePayload(variables);
    // capture the prior values of the fields we're about to change, so the edit is reversible
    let old: Record<string, unknown> | null = null;
    if (isUndoable(resource as string)) {
      try {
        const keys = Object.keys((cleanVars as any) || {});
        if (keys.length) {
          const cur: any = await directus.request(readItem(resource as any, id as string, { fields: keys } as any));
          old = {};
          for (const k of keys) old[k] = normalizeOld(cur?.[k]);
        }
      } catch { old = null; }
    }
    const data: any = await directus.request(updateItem(resource as any, id as string, cleanVars as any));
    if (old) pushUndo(inverseOfUpdate(resource as string, id, old), false);
    notifySaved();
    return { data };
  },

  deleteOne: async ({ resource, id }) => {
    // snapshot the record first so a delete can be re-created
    let rec: any = null;
    if (isUndoable(resource as string)) {
      try { rec = await directus.request(readItem(resource as any, id as string, { fields: ["*"] } as any)); } catch { rec = null; }
    }
    await directus.request(deleteItem(resource as any, id as string));
    if (rec) pushUndo(inverseOfDelete(resource as string, rec));
    return { data: { id } as any };
  },
};
